import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

export interface TeamMember {
  id: string;
  name: string;
  group: 'Product' | 'Design' | 'Engineering';
  practice: string;
}

export interface Pod {
  id: string;
  name: string;
  description: string;
  defaultVelocity: number;
  avgPointsPerEngineer: number;
  teamMembers: TeamMember[];
}

interface PodsState {
  pods: Pod[];
  unassignedMembers: TeamMember[];
}

const initialState: PodsState = {
  pods: [],
  unassignedMembers: [],
};

// Called inside Immer reducer context – direct mutation is intentional.
function recalculateVelocity(pod: Pod): void {
  if (pod.avgPointsPerEngineer > 0) {
    const engineerCount = pod.teamMembers.filter(m => m.group === 'Engineering').length;
    pod.defaultVelocity = pod.avgPointsPerEngineer * engineerCount;
  }
}

const podsSlice = createSlice({
  name: 'pods',
  initialState,
  reducers: {
    addPod: (state, action: PayloadAction<Omit<Pod, 'id'>>) => {
      state.pods.push({ ...action.payload, id: uuidv4() });
    },
    updatePod: (state, action: PayloadAction<Pod>) => {
      const idx = state.pods.findIndex(p => p.id === action.payload.id);
      if (idx !== -1) state.pods[idx] = action.payload;
    },
    deletePod: (state, action: PayloadAction<string>) => {
      const pod = state.pods.find(p => p.id === action.payload);
      if (pod && pod.teamMembers.length > 0) {
        state.unassignedMembers.push(...pod.teamMembers);
      }
      state.pods = state.pods.filter(p => p.id !== action.payload);
    },
    addTeamMember: (state, action: PayloadAction<{ podId: string; member: Omit<TeamMember, 'id'> }>) => {
      if (action.payload.podId === 'unassigned') {
        state.unassignedMembers.push({ ...action.payload.member, id: uuidv4() });
        return;
      }
      const pod = state.pods.find(p => p.id === action.payload.podId);
      if (pod) {
        pod.teamMembers.push({ ...action.payload.member, id: uuidv4() });
        recalculateVelocity(pod);
      }
    },
    updateTeamMember: (state, action: PayloadAction<{ podId: string; member: TeamMember }>) => {
      if (action.payload.podId === 'unassigned') {
        const idx = state.unassignedMembers.findIndex(m => m.id === action.payload.member.id);
        if (idx !== -1) state.unassignedMembers[idx] = action.payload.member;
        return;
      }
      const pod = state.pods.find(p => p.id === action.payload.podId);
      if (pod) {
        const idx = pod.teamMembers.findIndex(m => m.id === action.payload.member.id);
        if (idx !== -1) pod.teamMembers[idx] = action.payload.member;
      }
    },
    deleteTeamMember: (state, action: PayloadAction<{ podId: string; memberId: string }>) => {
      if (action.payload.podId === 'unassigned') {
        state.unassignedMembers = state.unassignedMembers.filter(m => m.id !== action.payload.memberId);
        return;
      }
      const pod = state.pods.find(p => p.id === action.payload.podId);
      if (pod) pod.teamMembers = pod.teamMembers.filter(m => m.id !== action.payload.memberId);
    },
    reorderTeamMembers: (state, action: PayloadAction<{ podId: string; members: TeamMember[] }>) => {
      if (action.payload.podId === 'unassigned') {
        state.unassignedMembers = action.payload.members;
        return;
      }
      const pod = state.pods.find(p => p.id === action.payload.podId);
      if (pod) pod.teamMembers = action.payload.members;
    },
    moveTeamMember: (state, action: PayloadAction<{ fromPodId: string; toPodId: string; member: TeamMember; destinationIndex?: number }>) => {
      const { fromPodId, toPodId, member, destinationIndex } = action.payload;
      if (fromPodId === 'unassigned') {
        state.unassignedMembers = state.unassignedMembers.filter(m => m.id !== member.id);
      } else {
        const fromPod = state.pods.find(p => p.id === fromPodId);
        if (fromPod) fromPod.teamMembers = fromPod.teamMembers.filter(m => m.id !== member.id);
      }
      const newMember = { ...member };
      if (toPodId === 'unassigned') {
        if (destinationIndex !== undefined) {
          state.unassignedMembers.splice(destinationIndex, 0, newMember);
        } else {
          state.unassignedMembers.push(newMember);
        }
      } else {
        const toPod = state.pods.find(p => p.id === toPodId);
        if (toPod) {
          if (destinationIndex !== undefined) {
            toPod.teamMembers.splice(destinationIndex, 0, newMember);
          } else {
            toPod.teamMembers.push(newMember);
          }
          recalculateVelocity(toPod);
        }
      }
      if (fromPodId !== 'unassigned') {
        const fromPod = state.pods.find(p => p.id === fromPodId);
        if (fromPod) recalculateVelocity(fromPod);
      }
    },
    setPods: (state, action: PayloadAction<Pod[]>) => {
      state.pods = action.payload;
      state.unassignedMembers = [];
    },
  },
});

export const {
  addPod, updatePod, deletePod,
  addTeamMember, updateTeamMember, deleteTeamMember,
  reorderTeamMembers, moveTeamMember, setPods,
} = podsSlice.actions;

export default podsSlice.reducer;
